// Mirrors EPN.Identity.Api's Features/Registration/RegistrationContracts.cs field-for-field. Keep the two
// in sync by hand (no shared OpenAPI/codegen exists in this repository yet).

export type WizardStepKey = 'company-info' | 'identity-verification' | 'address' | 'banking' | 'admin-info' | 'welcome';

// Internal screen order — matches the approved FINAL HTML's own six `.screen` elements exactly
// (companyInfoScreen, identityScreen, addressScreen, bankingScreen, adminScreen, welcomeScreen).
export const WIZARD_STEPS: { key: WizardStepKey; label: string }[] = [
  { key: 'company-info', label: 'Company Information' },
  { key: 'identity-verification', label: 'Identity Verification' },
  { key: 'address', label: 'Company Address' },
  { key: 'banking', label: 'Banking Details and Payroll Setup' },
  { key: 'admin-info', label: 'Admin Information' },
  { key: 'welcome', label: 'Welcome' },
];

/** The approved design's 4 primary, numbered left-panel steps ("mainSteps" in the FINAL HTML) — Identity
 * Verification is always a sub-step of Company Information (see `.substepper`), never a 5th numbered
 * step, and Welcome is a success screen with no left-panel step number at all. */
export const PRIMARY_STEPS: { key: WizardStepKey; label: string; description: string }[] = [
  { key: 'company-info', label: 'Company Information', description: 'Enter the basic details of the company' },
  { key: 'address', label: 'Company Address', description: 'Provide the official business location details' },
  { key: 'banking', label: 'Banking Details and Payroll Schedule', description: "Configure the company's banking information and payroll schedule" },
  { key: 'admin-info', label: 'Admin Information', description: 'Set up administrator account(s)' },
];

export function primaryStepFor(step: WizardStepKey): WizardStepKey {
  return step === 'identity-verification' ? 'company-info' : step;
}

// EPN-35 — Partner Sign Up. A parallel wizard, not a variant of WIZARD_STEPS/PRIMARY_STEPS above: it has
// its own leading client-only Account Setup screen (password collection moves to the front of the flow,
// opposite of Admin Sign Up's — see AccountSetupStep's own doc comment) and renames the final step from
// "Admin Information" to "Team Members". Every screen in between is the identical, unmodified Admin Sign Up
// component, reused via small optional props rather than duplicated.
export type PartnerWizardStepKey = 'account-setup' | 'partner-info' | 'identity-verification' | 'address' | 'banking' | 'team-members' | 'welcome';

export const PARTNER_WIZARD_STEPS: { key: PartnerWizardStepKey; label: string }[] = [
  { key: 'account-setup', label: 'Account Setup' },
  { key: 'partner-info', label: 'Partner Information' },
  { key: 'identity-verification', label: 'Identity Verification' },
  { key: 'address', label: 'Company Address' },
  { key: 'banking', label: 'Banking Details and Payroll Setup' },
  { key: 'team-members', label: 'Team Members' },
  { key: 'welcome', label: 'Welcome' },
];

/** 5 primary, numbered left-panel steps — Identity Verification is a sub-step of Partner Information, same
 * pattern as Admin Sign Up's own substepper, never a 6th numbered step. */
export const PARTNER_PRIMARY_STEPS: { key: PartnerWizardStepKey; label: string; description: string }[] = [
  { key: 'account-setup', label: 'Account Setup', description: 'Create your sign-in email and password' },
  { key: 'partner-info', label: 'Partner Information', description: 'Enter the basic details of your partner business' },
  { key: 'address', label: 'Company Address', description: 'Provide the official business location details' },
  { key: 'banking', label: 'Banking Details and Payroll Schedule', description: "Configure the company's banking information and payroll schedule" },
  { key: 'team-members', label: 'Team Members', description: 'Set up additional team member accounts' },
];

export function partnerPrimaryStepFor(step: PartnerWizardStepKey): PartnerWizardStepKey {
  return step === 'identity-verification' ? 'partner-info' : step;
}

export interface CreateRegistrationRequest {
  companyName: string;
  ein: string | null;
  sein: string | null;
  website: string | null;
  contactEmail: string | null;
  contactPhone: string | null;
  employeeCount: number | null;
  /** EPN-35 — optional, defaults server-side to "Admin" when omitted. Admin Sign Up never sends this. */
  channel?: 'Admin' | 'Partner';
  /** EPN-35 Partner Sign Up only — "Ssn" | "Ein", matching EPN.Employees.Api's own ContractorIdentificationType
   * shape (the one existing precedent in this codebase for this kind of identifier). */
  identificationType?: 'Ssn' | 'Ein' | null;
  /** EPN-35 Partner Sign Up only — the full value as entered. Never persisted or returned in full by any
   * endpoint; the server keeps only its last 4 digits (see CompanyLogoResponse's sibling,
   * Company.IdentificationLast4). Treat as sensitive — the same as any other SSN/EIN value in this app. */
  identificationNumber?: string | null;
  /** EPN-35 Partner Sign Up only. */
  faxNumber?: string | null;
}

/** EPN-35 Partner Sign Up only — the optional company logo upload's response. */
export interface CompanyLogoResponse {
  storedFileName: string;
  contentType: string;
  sizeBytes: number;
}

export interface RegistrationCreatedResponse {
  registrationId: string;
  companyId: string;
  onboardingTokenExpiresAtUtc: string;
}

export interface UpsertCompanyAddressRequest {
  branchName: string | null;
  billingAddressLine1: string;
  billingAddressLine2: string | null;
  billingCountry: string;
  billingCity: string;
  billingState: string;
  billingZip: string;
  mailingSameAsBilling: boolean;
  mailingAddressLine1: string | null;
  mailingAddressLine2: string | null;
  mailingCountry: string | null;
  mailingCity: string | null;
  mailingState: string | null;
  mailingZip: string | null;
}

export type CompanyAddressResponse = UpsertCompanyAddressRequest;

export interface CompanyVerificationDocumentResponse {
  id: string;
  originalFileName: string;
  contentType: string;
  sizeBytes: number;
  uploadedAtUtc: string;
}

export type IdentityVerificationStatus = 'NotStarted' | 'Submitted' | 'Skipped' | 'Verified';

export interface CompanyVerificationStatusResponse {
  status: IdentityVerificationStatus;
  documentType: string | null;
  missingDocumentReason: string | null;
  hasLivePhoto: boolean;
  documents: CompanyVerificationDocumentResponse[];
}

export interface CreateOwnerRequest {
  email: string;
  password: string;
  displayName: string | null;
  officeNumber: string | null;
  phone: string | null;
}

export interface OwnerResponse {
  userId: string;
  email: string;
}

export interface AssignableRoleResponse {
  id: string;
  name: string;
}

export interface CreateInvitationRequest {
  email: string;
  name: string | null;
  roleId: string;
  isOwner: boolean;
  officeNumber: string | null;
  phone: string | null;
}

export interface InvitationResponse {
  id: string;
  inviteeEmail: string;
  name: string | null;
  roleId: string;
  isOwner: boolean;
  officeNumber: string | null;
  phone: string | null;
  status: 'Pending' | 'Accepted' | 'Revoked';
  expiresAtUtc: string;
}

export interface CreateFundingAccountRequest {
  bankName: string;
  routingNumber: string;
  accountNumber: string;
  accountType: 'Checking' | 'Savings';
}

export interface FundingAccountResponse {
  id: string;
  bankName: string;
  maskedAccountLast4: string;
  accountType: string;
  fundingPartner: string;
  verificationState: string;
}

// Mirrors EPN.PayrollConfig.Api's PayDayKind enum exactly (DayOfMonth=1, LastDayOfMonth=2). When a day is
// "Last Day of Month", the paired *PayDay value must be null — never a magic sentinel number.
export const PAY_DAY_KIND = { DayOfMonth: 1, LastDayOfMonth: 2 } as const;

export interface CreatePayScheduleRequest {
  name: string;
  frequency: number;
  firstPayDay: number | null;
  firstPayDayKind: number | null;
  secondPayDay: number | null;
  secondPayDayKind: number | null;
  payPeriodStartDate: string | null;
}

export interface PayScheduleResponse {
  id: string;
  name: string;
  frequency: number;
  firstPayDay: number | null;
  firstPayDayKind: number;
  secondPayDay: number | null;
  secondPayDayKind: number | null;
  payPeriodStartDate: string | null;
  isDefault: boolean;
  isActive: boolean;
}

export interface RegistrationStatusResponse {
  registrationId: string;
  companyId: string;
  registrationStatus: 'InProgress' | 'Finalized';
  hasAddress: boolean;
  verificationStatus: IdentityVerificationStatus;
  hasOwner: boolean;
  invitationCount: number;
  hasPaySchedule: boolean;
  hasFundingAccount: boolean;
  fundingPartner: string | null;
}

export interface FinalizeRegistrationResponse {
  finalized: boolean;
  finalizedAtUtc: string;
}

export type InvitationValidity = 'Valid' | 'Expired' | 'AlreadyUsed' | 'NotFound';

// inviteeHasExistingAccount is only ever meaningful (and only ever true/false, never omitted) once
// validity is 'Valid' — see RegistrationContracts.InvitationValidityResponse's own doc comment on why this
// can never be used to probe whether an arbitrary email already has an EPN account.
export interface InvitationValidityResponse {
  validity: InvitationValidity;
  inviteeEmail: string | null;
  inviteeHasExistingAccount: boolean;
}
